import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Plus, PencilSimple, Trash, Clock, User, CurrencyInr } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  { key: 'draft',            label: 'Draft',           color: '#64748b', bg: '#f1f5f9' },
  { key: 'in_review',        label: 'In Review',       color: '#0369a1', bg: '#e0f2fe' },
  { key: 'ready_for_export', label: 'Ready',           color: '#7c3aed', bg: '#ede9fe' },
  { key: 'sent_to_client',   label: 'Sent to Client',  color: '#d97706', bg: '#fef3c7' },
  { key: 'approved',         label: 'Approved',        color: '#16a34a', bg: '#dcfce7' },
];

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const daysSince = (iso) => {
  if (!iso) return 0;
  const d = new Date(iso);
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

const ProposalsKanbanPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSeePricing = user?.role === 'admin';
  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingId, setDraggingId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    try {
      setLoading(true);
      const [p, c] = await Promise.all([
        axios.get(`${API_URL}/api/proposals`, { withCredentials: true }),
        axios.get(`${API_URL}/api/clients`, { withCredentials: true }),
      ]);
      setProposals(p.data || []);
      setClients(c.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh on focus / visibility change so newly submitted proposals appear immediately
  useEffect(() => {
    const onFocus = () => load();
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    const interval = setInterval(load, 30000); // also poll every 30s
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(interval);
    };
  }, []);

  const clientMap = useMemo(() => {
    const m = {};
    clients.forEach((c) => { m[c.id] = c; });
    return m;
  }, [clients]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    proposals.forEach((p) => {
      // status filter (column toggle)
      if (statusFilter !== 'all' && p.status !== statusFilter) return;
      // text search across title, number, client name/company
      if (q) {
        const cl = clientMap[p.clientId] || {};
        const clientName = cl.company_name || `${cl.first_name || ''} ${cl.last_name || ''}`.trim();
        const blob = `${p.title || ''} ${p.number || ''} ${p.version || ''} ${clientName}`.toLowerCase();
        if (!blob.includes(q)) return;
      }
      const s = COLUMNS.find((c) => c.key === p.status) ? p.status : 'draft';
      out[s].push(p);
    });
    // Sort each column by updated_at desc
    Object.keys(out).forEach((k) => {
      out[k].sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''));
    });
    return out;
  }, [proposals, clientMap, search, statusFilter]);

  const totalValue = (col) => grouped[col].reduce((s, p) => s + Number(p?.pricing?.final || 0), 0);

  const moveProposal = async (id, newStatus) => {
    setProposals((prev) => prev.map((p) => p.id === id ? { ...p, status: newStatus } : p));
    try {
      const p = proposals.find((x) => x.id === id);
      if (!p) return;
      await axios.put(`${API_URL}/api/proposals/${id}`, { ...p, status: newStatus }, { withCredentials: true });
    } catch (e) {
      load(); // revert by reloading
    }
  };

  const onDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };
  const onDragEnd = () => { setDraggingId(null); setOverCol(null); };
  const onDragOverCol = (e, key) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOverCol(key); };
  const onDropCol = (e, key) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || draggingId;
    setOverCol(null); setDraggingId(null);
    if (!id) return;
    const cur = proposals.find((x) => x.id === id);
    if (cur && cur.status !== key) moveProposal(id, key);
  };

  const openBuilder = (id) => navigate(`/proposals/builder${id ? `?id=${encodeURIComponent(id)}` : ''}`);

  const newProposal = async () => {
    const p = {
      id: 'p_' + Date.now(),
      title: 'New Proposal',
      status: 'draft',
      clientId: null,
      date: new Date().toISOString().slice(0, 10),
      validity: '30 days', version: '1', number: '',
      isSA: true,
      scope: [{ phase: 'Phase 1', task: '', days: '' }],
      tech: [{ item: '' }],
      pricing: { currency: 'INR', rows: [{ desc: '', amount: 0 }], discount: 0, tax: 0, final: 0, terms: '50% advance + 50% on delivery', note: '' },
      sections: {
        confidentiality: 'The information contained in this document is confidential and proprietary to Dextra Technologies. [CLIENT] will hold it in strict confidence.',
        acknowledgment: 'We would like to thank [CLIENT] for giving Dextra Technologies the opportunity to submit this proposal.',
        disclaimer: 'The obligation of the parties to perform the effort identified in this document is subject to the execution of a written agreement.',
        about: 'Dextra Technologies is leading Software Solutions, Services & Products Company based in Chennai, India.',
        background: 'Dextra Technologies welcomes the opportunity to submit this proposal for [CLIENT].',
        assumptions: ['Work will be done offsite at Dextra\'s office.', 'Client will provide required content and access.', 'Hardware and software costs will be borne by the Client.'],
        notInScope: ['Data Migration', 'Performance Testing', 'Any functionality not mentioned in scope'],
        whyDextra: 'Dextra Technologies — established 2015, Chennai. "We want to add wings to your business, so you can fly high."',
        portfolio: 'Sample works available at www.dextratechnologies.com',
      },
    };
    try {
      await axios.post(`${API_URL}/api/proposals`, p, { withCredentials: true });
      openBuilder(p.id);
    } catch (e) { alert('Could not create proposal'); }
  };

  const deleteProposal = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this proposal?')) return;
    await axios.delete(`${API_URL}/api/proposals/${id}`, { withCredentials: true });
    setProposals((prev) => prev.filter((p) => p.id !== id));
  };

  const totalCount = proposals.length;
  const grandTotal = proposals.reduce((s, p) => s + Number(p?.pricing?.final || 0), 0);

  return (
    <div data-testid="proposals-kanban-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>Proposal Pipeline</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>
            {totalCount} proposals{canSeePricing && <> · pipeline value <strong style={{ color: 'var(--dx-text)' }}>{fmtINR(grandTotal)}</strong></>} · drag a card to change status
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openBuilder()} data-testid="open-builder-btn">
            <PencilSimple size={16} className="mr-1.5" /> Open Builder
          </Button>
          <Button className="text-white" style={{ background: 'var(--dx-primary)' }} onClick={newProposal} data-testid="new-proposal-btn">
            <Plus size={18} className="mr-1" /> New Proposal
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm" style={{ color: 'var(--dx-text3)' }}>Loading pipeline...</div>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap bg-white p-2 rounded-md border" style={{ borderColor: 'var(--dx-border)' }}>
            <input
              type="text"
              placeholder="Search by title, proposal #, client…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="text-sm px-3 py-2 border rounded outline-none focus:ring-2 focus:ring-emerald-500/30 flex-1 min-w-[240px]"
              data-testid="proposal-search"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm px-2 py-2 border rounded bg-white"
              data-testid="proposal-status-filter"
            >
              <option value="all">All Status</option>
              {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            {(search || statusFilter !== 'all') && (
              <button
                type="button"
                onClick={() => { setSearch(''); setStatusFilter('all'); }}
                className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
                data-testid="proposal-clear-filters"
              >Clear</button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kanban-board">
          {COLUMNS.map((col) => (
            <div
              key={col.key}
              onDragOver={(e) => onDragOverCol(e, col.key)}
              onDragLeave={() => setOverCol((k) => (k === col.key ? null : k))}
              onDrop={(e) => onDropCol(e, col.key)}
              data-testid={`col-${col.key}`}
              className="rounded-lg flex flex-col"
              style={{
                background: col.bg,
                minHeight: 480,
                border: overCol === col.key ? `2px dashed ${col.color}` : '2px solid transparent',
                transition: 'border-color 120ms',
              }}
            >
              <div className="px-3 py-2.5 flex items-center justify-between sticky top-0 rounded-t-lg" style={{ background: col.bg, borderBottom: `2px solid ${col.color}` }}>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                  <span className="font-semibold text-sm" style={{ color: col.color }}>{col.label}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={{ background: col.color, color: '#fff' }} data-testid={`count-${col.key}`}>
                    {grouped[col.key].length}
                  </span>
                </div>
                <div className="text-[10px] font-semibold" style={{ color: col.color }}>{canSeePricing ? fmtINR(totalValue(col.key)) : ''}</div>
              </div>

              <div className="p-2 space-y-2 flex-1">
                {grouped[col.key].length === 0 && (
                  <div className="text-xs text-center py-6" style={{ color: '#94a3b8' }}>Drop here</div>
                )}
                {grouped[col.key].map((p) => {
                  const cl = clientMap[p.clientId] || {};
                  const clientName = cl.company_name || `${cl.first_name || ''} ${cl.last_name || ''}`.trim() || '— No client —';
                  const value = Number(p?.pricing?.final || 0);
                  const age = daysSince(p.updated_at || p.created_at);
                  const isOld = age > 7 && col.key !== 'approved';
                  return (
                    <div
                      key={p.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, p.id)}
                      onDragEnd={onDragEnd}
                      onClick={() => openBuilder(p.id)}
                      data-testid={`card-${p.id}`}
                      className="bg-white rounded-md p-3 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow border"
                      style={{
                        borderLeft: `3px solid ${col.color}`,
                        opacity: draggingId === p.id ? 0.4 : 1,
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-sm leading-snug line-clamp-2" style={{ color: 'var(--dx-text)' }}>{p.title || 'Untitled'}</div>
                        <button
                          onClick={(e) => deleteProposal(e, p.id)}
                          className="text-red-400 hover:text-red-600 flex-shrink-0"
                          title="Delete"
                          data-testid={`del-${p.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                      <div className="mt-2 flex items-center gap-1 text-xs" style={{ color: 'var(--dx-text2)' }}>
                        <User size={12} /> <span className="truncate">{clientName}</span>
                      </div>
                      {canSeePricing && value > 0 && (
                        <div className="mt-1 flex items-center gap-1 text-xs font-semibold" style={{ color: col.color }}>
                          <CurrencyInr size={12} /> {fmtINR(value)}
                        </div>
                      )}
                      <div className="mt-2 flex items-center justify-between text-[10px]" style={{ color: isOld ? '#dc2626' : '#94a3b8' }}>
                        <span className="flex items-center gap-1"><Clock size={11} /> {age === 0 ? 'today' : `${age}d ago`}</span>
                        <span>{p.number || `v${p.version || '1'}`}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
};

export default ProposalsKanbanPage;
